import { useTranslation } from 'react-i18next';
import type { Card } from '@asia/shared';
import { haptic } from '../../hooks/useTelegram';
import { useBackHandler } from '../../hooks/useBackHandler';
import { copyText } from '../../lib/clipboard';
import { FigIcon } from '../FigIcon';
import visaCard from '../../assets/figma/visa-card.png';

interface Props {
  cards: Card[];
  onPick: (id: string) => void;
  onAdd: () => void;
  onClose: () => void;
}

/** Mini card thumbnail (48×38) — the exact Figma VISA card image (node 2:4924). */
function CardThumb() {
  return (
    <img
      src={visaCard}
      alt="VISA"
      className="h-[38px] w-[48px] shrink-0 rounded-[9px] object-cover"
    />
  );
}

/** Card-selector popover anchored under the balance card — Figma node 2:4919:
 *  translucent sheet, one row per card (mini card + name + number + chevron),
 *  a divider, then "Add card". */
export function CardSelector({ cards, onPick, onAdd, onClose }: Props) {
  const { t } = useTranslation();
  // The native Telegram BackButton closes the popover.
  useBackHandler(true, onClose);
  return (
    <>
      {/* tap anywhere outside the popover closes it */}
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />
      <div
        className="fade-in absolute left-1/2 top-[calc(100%-18px)] z-50 flex w-[300px] -translate-x-1/2 flex-col gap-4 rounded-[28px] p-4 backdrop-blur-[2px]"
        style={{
          backgroundColor: 'rgb(var(--sheet) / 0.8)',
          boxShadow: '0px 0px 40px 0px rgba(0,0,0,0.12)',
        }}
      >
        {cards.map((c) => (
          <div key={c.id} className="flex h-9 w-full items-center justify-between">
            <button
              onClick={() => {
                haptic();
                onPick(c.id);
              }}
              className="flex min-w-0 flex-1 items-center gap-[6px] text-left"
            >
              <CardThumb />
              <span className="flex min-w-0 flex-col gap-[5px]">
                <span className="flex items-center gap-[2px]">
                  <span className="truncate text-[15px] font-medium text-ink">{c.label}</span>
                  <span
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void copyText(c.number ?? c.last4);
                    }}
                  >
                    <FigIcon name="copy" size={16} className="text-muted" />
                  </span>
                </span>
                <span className="flex items-center gap-[5px] text-[13px] text-muted">
                  ····{c.last4}
                  <span className="inline-block size-[2px] rounded-full bg-muted" />
                  {String(c.expMonth).padStart(2, '0')}/{c.expYear}
                </span>
              </span>
            </button>
            <FigIcon name="arrowRight" size={16} className="ml-2 text-muted" />
          </div>
        ))}

        <div className="h-px w-full bg-black/10" />

        <button
          onClick={() => {
            haptic();
            onAdd();
          }}
          className="flex items-center justify-center gap-[6px] active:opacity-70"
        >
          <FigIcon name="add" size={20} className="text-ink" />
          <span className="text-[14px] text-ink">{t('cards.issue')}</span>
        </button>
      </div>
    </>
  );
}
